// ─── Le Fil — réseau social d'entreprise ─────────────────────────────────────
// Fil d'actualité du groupe : composeur en tête, puces de filtre, cartes de
// publication (texte, photos/vidéo, lien, réactions, commentaires), défilement
// infini et bandeau « N nouvelles publications » en temps réel (SSE).
// Tout le texte saisi est rendu en éléments React : jamais de HTML injecté.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import filApi from '../../services/fil';
import { compressFile } from '../../utils/documents';
import './Fil.css';

const EMOJIS = ['👍', '❤️', '👏', '🎉'];
const ACTIVITE_LABEL = { auto: 'Automobile', boulangerie: 'Boulangerie', agriculture: 'Agriculture', autre: 'Autre' };
const TYPES_IMAGE = ['image/jpeg', 'image/png'];
const TYPES_VIDEO = ['video/mp4', 'video/webm', 'video/quicktime'];
const PHOTOS_MAX = 10;
const VIDEO_DUREE_MAX_S = 90;
const VIDEO_TAILLE_MAX = 100 * 1024 * 1024;

// ─── Petits utilitaires d'affichage ──────────────────────────────────────────

const initiales = (a) => `${(a?.prenom || '')[0] || ''}${(a?.nom || '')[0] || ''}`.toUpperCase() || '?';
const nomAffiche = (a) => [a?.prenom, a?.nom].filter(Boolean).join(' ') || 'Collaborateur';
// Une couleur d'avatar stable par concession (8 teintes), lisible sur les deux thèmes.
const couleurAvatar = (id) => `hsl(${((Number(id) || 0) * 47 + 210) % 360} 55% 45%)`;

function dateRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'à l’instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
}

const dureeVideo = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '');

// Texte → éléments React : URLs cliquables, sauts de ligne conservés par CSS
// (white-space: pre-line). Aucun HTML n'est interprété.
const RE_URL = /(https?:\/\/[^\s<>"']+)/g;
function TexteRiche({ texte }) {
  if (!texte) return null;
  const parts = texte.split(RE_URL);
  return (
    <p className="fil-txt">
      {parts.map((p, i) => (i % 2 === 1
        ? <a key={i} href={p} target="_blank" rel="noopener noreferrer">{p.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>
        : p))}
    </p>
  );
}

function Avatar({ personne, petit }) {
  return (
    <span className={`fil-av${petit ? ' petit' : ''}`} style={{ background: couleurAvatar(personne?.concession_id) }} title={nomAffiche(personne)}>
      {initiales(personne)}
    </span>
  );
}

function TagPortee({ post }) {
  if (post.portee === 'groupe') return <span className="fil-tag t-groupe">Tout le groupe</span>;
  if (post.portee === 'activite') return <span className="fil-tag t-activite">{ACTIVITE_LABEL[post.activite] || post.activite}</span>;
  return <span className="fil-tag t-site">{post.concession_nom || 'Concession'}</span>;
}

// ─── Médias : mosaïque + visionneuse ─────────────────────────────────────────

function Mosaique({ medias, onOuvrir }) {
  if (!medias?.length) return null;
  const visibles = medias.slice(0, 4);
  const reste = medias.length - visibles.length;
  return (
    <div className={`fil-media m${visibles.length}`}>
      {visibles.map((m, i) => (
        <button type="button" key={m.id} className={`fil-ph${m.content_type.startsWith('video/') ? ' video' : ''}`} onClick={() => onOuvrir(i)} aria-label={`Ouvrir le média ${i + 1}`}>
          {m.content_type.startsWith('video/')
            ? <video src={m.url} preload="metadata" muted playsInline />
            : <img src={m.url} alt="" loading="lazy" />}
          {m.content_type.startsWith('video/') && <span className="fil-play">▶</span>}
          {m.content_type.startsWith('video/') && m.duree_s ? <span className="fil-dur">{dureeVideo(m.duree_s)}</span> : null}
          {i === visibles.length - 1 && reste > 0 && <span className="fil-more">+{reste}</span>}
        </button>
      ))}
    </div>
  );
}

function Visionneuse({ medias, index, onFermer, onNaviguer }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onFermer();
      if (e.key === 'ArrowRight') onNaviguer(1);
      if (e.key === 'ArrowLeft') onNaviguer(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer, onNaviguer]);
  const m = medias[index];
  if (!m) return null;
  return (
    <div className="fil-lightbox" onClick={onFermer} role="dialog" aria-label="Visionneuse">
      <button type="button" className="fil-lb-close" onClick={onFermer} aria-label="Fermer">✕</button>
      {medias.length > 1 && <button type="button" className="fil-lb-nav prev" onClick={(e) => { e.stopPropagation(); onNaviguer(-1); }} aria-label="Précédent">‹</button>}
      <div className="fil-lb-body" onClick={(e) => e.stopPropagation()}>
        {m.content_type.startsWith('video/')
          ? <video src={m.url} controls autoPlay playsInline />
          : <img src={m.url} alt="" />}
      </div>
      {medias.length > 1 && <button type="button" className="fil-lb-nav next" onClick={(e) => { e.stopPropagation(); onNaviguer(1); }} aria-label="Suivant">›</button>}
      <div className="fil-lb-count">{index + 1} / {medias.length}</div>
    </div>
  );
}

function CarteLien({ lien, onRetirer }) {
  if (!lien?.url) return null;
  return (
    <a className="fil-link" href={lien.url} target="_blank" rel="noopener noreferrer">
      {lien.image ? <span className="fil-link-im" style={{ backgroundImage: `url("${lien.image.replace(/"/g, '%22')}")` }} /> : <span className="fil-link-im vide">🔗</span>}
      <span className="fil-link-b">
        <span className="fil-link-d">{lien.site || (() => { try { return new URL(lien.url).hostname; } catch { return ''; } })()}</span>
        <span className="fil-link-t">{lien.titre || lien.url}</span>
        {lien.description && <span className="fil-link-s">{lien.description}</span>}
      </span>
      {onRetirer && <button type="button" className="fil-link-x" onClick={(e) => { e.preventDefault(); onRetirer(); }} aria-label="Retirer le lien">✕</button>}
    </a>
  );
}

// ─── Composeur ───────────────────────────────────────────────────────────────

function lireDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    if (file.type.startsWith('video/')) {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve({ largeur: v.videoWidth, hauteur: v.videoHeight, duree_s: Math.round(v.duration || 0) }); };
      v.onerror = () => { URL.revokeObjectURL(url); resolve({}); };
      v.src = url;
    } else {
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve({ largeur: img.naturalWidth, hauteur: img.naturalHeight }); };
      img.onerror = () => { URL.revokeObjectURL(url); resolve({}); };
      img.src = url;
    }
  });
}

function envoyerVersS3(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Réseau'));
    xhr.send(file);
  });
}

function Composeur({ contexte, onPublie, ouvertInitial }) {
  const { moi, porteesAutorisees, concessions } = contexte;
  const [ouvert, setOuvert] = useState(!!ouvertInitial);
  const [texte, setTexte] = useState('');
  // Les rôles globaux publient d'abord pour le groupe ; les autres pour leur site.
  const [portee, setPortee] = useState(porteesAutorisees.includes('groupe') ? 'groupe' : (porteesAutorisees[0] || 'concession'));
  const [concessionId, setConcessionId] = useState(moi.concession_id || concessions[0]?.id || '');
  const [activite, setActivite] = useState(moi.activite || contexte.activites[0] || 'auto');
  const [fichiers, setFichiers] = useState([]);       // { file, apercu, info }
  const [lien, setLien] = useState(null);
  const [lienSaisie, setLienSaisie] = useState('');
  const [lienOuvert, setLienOuvert] = useState(false);
  const [lienChargement, setLienChargement] = useState(false);
  const [envoi, setEnvoi] = useState(null);           // { etape, progression }
  const [erreur, setErreur] = useState('');
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { if (ouvertInitial) { setOuvert(true); textareaRef.current?.focus(); } }, [ouvertInitial]);
  useEffect(() => () => fichiers.forEach((f) => URL.revokeObjectURL(f.apercu)), [fichiers]);

  const nbVideos = fichiers.filter((f) => f.file.type.startsWith('video/')).length;
  const nbPhotos = fichiers.length - nbVideos;
  const nomPortee = (p) => {
    if (p === 'concession') return moi.global ? 'Une concession' : (moi.concession_nom || 'Ma concession');
    if (p === 'activite') return moi.global ? 'Une activité' : `Activité ${ACTIVITE_LABEL[moi.activite] || moi.activite || ''}`;
    return 'Tout le groupe';
  };

  const ajouterFichiers = async (liste) => {
    setErreur('');
    const ajouts = [];
    for (const file of Array.from(liste || [])) {
      const estVideo = TYPES_VIDEO.includes(file.type);
      if (!TYPES_IMAGE.includes(file.type) && !estVideo) { setErreur(`« ${file.name} » : formats acceptés JPEG, PNG, MP4, WebM, MOV.`); continue; }
      if (estVideo && (nbVideos + ajouts.filter((a) => a.file.type.startsWith('video/')).length) >= 1) { setErreur('Une seule vidéo par publication.'); continue; }
      if (!estVideo && (nbPhotos + ajouts.filter((a) => !a.file.type.startsWith('video/')).length) >= PHOTOS_MAX) { setErreur(`${PHOTOS_MAX} photos au maximum.`); continue; }
      if (estVideo && file.size > VIDEO_TAILLE_MAX) { setErreur(`« ${file.name} » dépasse 100 Mo.`); continue; }
      const info = await lireDimensions(file);
      if (estVideo && info.duree_s > VIDEO_DUREE_MAX_S) { setErreur(`« ${file.name} » dure ${dureeVideo(info.duree_s)} : maximum 1 min 30.`); continue; }
      ajouts.push({ file, apercu: URL.createObjectURL(file), info });
    }
    if (ajouts.length) setFichiers((prev) => [...prev, ...ajouts]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const retirerFichier = (i) => setFichiers((prev) => { URL.revokeObjectURL(prev[i].apercu); return prev.filter((_, j) => j !== i); });

  const chargerLien = async () => {
    const url = lienSaisie.trim();
    if (!url) return;
    setLienChargement(true); setErreur('');
    try {
      const { data } = await filApi.unfurl(/^https?:\/\//i.test(url) ? url : `https://${url}`);
      setLien(data.apercu); setLienOuvert(false); setLienSaisie('');
    } catch (e) {
      setErreur(e.response?.data?.error || 'Lien non exploitable');
    } finally { setLienChargement(false); }
  };

  const reinitialiser = () => {
    setTexte(''); setFichiers([]); setLien(null); setLienSaisie(''); setLienOuvert(false); setEnvoi(null); setErreur(''); setOuvert(false);
  };

  const publier = async () => {
    if (!texte.trim() && fichiers.length === 0 && !lien) { setErreur('Écrivez quelque chose, ajoutez une photo ou un lien.'); return; }
    setErreur(''); setEnvoi({ etape: 'Création…', progression: 0 });
    try {
      const { data } = await filApi.createPost({
        texte: texte.trim(), portee,
        concession_id: portee === 'concession' ? Number(concessionId) || undefined : undefined,
        activite: portee === 'activite' ? activite : undefined,
        lien: lien || undefined, nb_medias: fichiers.length,
      });
      const postId = data.post.id;
      if (fichiers.length > 0) {
        const confirmes = [];
        for (let i = 0; i < fichiers.length; i++) {
          const { file, info } = fichiers[i];
          setEnvoi({ etape: `Envoi ${i + 1}/${fichiers.length}…`, progression: i / fichiers.length });
          const pret = file.type.startsWith('image/') ? await compressFile(file) : file;
          const { data: p } = await filApi.presignMedia(postId, { filename: pret.name, content_type: pret.type, size: pret.size });
          await envoyerVersS3(p.uploadUrl, pret, (r) => setEnvoi({ etape: `Envoi ${i + 1}/${fichiers.length}…`, progression: (i + r) / fichiers.length }));
          confirmes.push({ key: p.key, content_type: pret.type, ...info });
        }
        setEnvoi({ etape: 'Publication…', progression: 1 });
        await filApi.confirmMedias(postId, confirmes);
        await filApi.publier(postId);
      }
      reinitialiser();
      onPublie(postId);
    } catch (e) {
      setEnvoi(null);
      setErreur(e.response?.data?.error || e.message || 'Échec de la publication');
    }
  };

  return (
    <div className={`fil-card fil-composer${ouvert ? ' ouvert' : ''}`}>
      <div className="fil-comp-row">
        <Avatar personne={moi} />
        {!ouvert ? (
          <button type="button" className="fil-comp-fake" onClick={() => { setOuvert(true); setTimeout(() => textareaRef.current?.focus(), 0); }}>
            Quoi de neuf {moi.concession_nom ? `chez ${moi.concession_nom}` : 'dans le groupe'} ?
          </button>
        ) : (
          <div className="fil-comp-who">
            <b>{nomAffiche(moi)}</b>
            <span>publie pour</span>
            <div className="fil-scope" role="radiogroup" aria-label="Portée">
              {porteesAutorisees.map((p) => (
                <button type="button" key={p} className={portee === p ? 'on' : ''} onClick={() => setPortee(p)} role="radio" aria-checked={portee === p}>
                  {nomPortee(p)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {ouvert && (
        <div className="fil-comp-open">
          {moi.global && portee === 'concession' && (
            <select className="fil-select" value={concessionId} onChange={(e) => setConcessionId(e.target.value)} aria-label="Concession">
              {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          )}
          {moi.global && portee === 'activite' && (
            <select className="fil-select" value={activite} onChange={(e) => setActivite(e.target.value)} aria-label="Activité">
              {contexte.activites.map((a) => <option key={a} value={a}>{ACTIVITE_LABEL[a] || a}</option>)}
            </select>
          )}
          <textarea
            ref={textareaRef}
            value={texte}
            onChange={(e) => setTexte(e.target.value.slice(0, 5000))}
            placeholder="Une nouvelle, une réussite, un événement, un merci…"
            rows={3}
            disabled={!!envoi}
          />

          {fichiers.length > 0 && (
            <div className="fil-comp-files">
              {fichiers.map((f, i) => (
                <div key={f.apercu} className="fil-comp-file">
                  {f.file.type.startsWith('video/') ? <video src={f.apercu} muted playsInline /> : <img src={f.apercu} alt="" />}
                  {f.file.type.startsWith('video/') && <span className="fil-dur">{dureeVideo(f.info.duree_s)}</span>}
                  {!envoi && <button type="button" onClick={() => retirerFichier(i)} aria-label="Retirer">✕</button>}
                </div>
              ))}
            </div>
          )}

          {lien && <CarteLien lien={lien} onRetirer={envoi ? null : () => setLien(null)} />}

          {lienOuvert && !lien && (
            <div className="fil-comp-lien">
              <input
                type="url" value={lienSaisie} onChange={(e) => setLienSaisie(e.target.value)} placeholder="https://…"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); chargerLien(); } }} autoFocus
              />
              <button type="button" className="fil-tool" onClick={chargerLien} disabled={lienChargement}>{lienChargement ? '…' : 'Ajouter'}</button>
              <button type="button" className="fil-tool" onClick={() => { setLienOuvert(false); setLienSaisie(''); }}>Annuler</button>
            </div>
          )}

          {erreur && <div className="fil-erreur">{erreur}</div>}

          {envoi ? (
            <div className="fil-progress" aria-live="polite">
              <div className="fil-progress-bar" style={{ width: `${Math.round(envoi.progression * 100)}%` }} />
              <span>{envoi.etape}</span>
            </div>
          ) : (
            <div className="fil-tools">
              <input ref={inputRef} type="file" accept={[...TYPES_IMAGE, ...TYPES_VIDEO].join(',')} multiple hidden onChange={(e) => ajouterFichiers(e.target.files)} />
              <button type="button" className="fil-tool" onClick={() => inputRef.current?.click()}>📷 Photos</button>
              <button type="button" className="fil-tool" onClick={() => inputRef.current?.click()} disabled={nbVideos >= 1}>🎬 Vidéo</button>
              <button type="button" className="fil-tool" onClick={() => setLienOuvert(true)} disabled={!!lien}>🔗 Lien</button>
              <button type="button" className="fil-tool fil-tool-annuler" onClick={reinitialiser}>Annuler</button>
              <button type="button" className="fil-tool primaire" onClick={publier}>Publier</button>
            </div>
          )}
          <div className="fil-hint">Photos et vidéos : ne publiez que des collègues d’accord pour apparaître. Visible par : {nomPortee(portee)}.</div>
        </div>
      )}
    </div>
  );
}

// ─── Commentaires ────────────────────────────────────────────────────────────

function Commentaires({ post, moi, onChange }) {
  const [tous, setTous] = useState(null);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const liste = tous || post.commentaires;
  const caches = post.nb_commentaires - liste.length;

  const voirTout = async () => {
    try { const { data } = await filApi.getCommentaires(post.id); setTous(data.commentaires); } catch { /* silencieux */ }
  };
  const envoyer = async () => {
    const t = saisie.trim();
    if (!t || envoi) return;
    setEnvoi(true);
    try {
      const { data } = await filApi.addCommentaire(post.id, t);
      setSaisie('');
      const nouvelle = [...liste, data.commentaire];
      setTous(nouvelle);
      onChange({ commentaires: nouvelle.slice(-2), nb_commentaires: post.nb_commentaires + 1 });
    } catch (e) { alert(e.response?.data?.error || 'Commentaire non envoyé'); }
    finally { setEnvoi(false); }
  };
  const supprimer = async (c) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await filApi.removeCommentaire(c.id);
      const reste = liste.filter((x) => x.id !== c.id);
      setTous(reste);
      onChange({ commentaires: reste.slice(-2), nb_commentaires: Math.max(0, post.nb_commentaires - 1) });
    } catch (e) { alert(e.response?.data?.error || 'Suppression impossible'); }
  };

  return (
    <div className="fil-comments">
      {caches > 0 && <button type="button" className="fil-voir-tout" onClick={voirTout}>Voir les {post.nb_commentaires} commentaires</button>}
      {liste.map((c) => (
        <div key={c.id} className="fil-co">
          <Avatar personne={c} petit />
          <div className="fil-bub">
            <b>{nomAffiche(c)}</b>{c.texte}
            <small>
              {c.concession_nom ? `${c.concession_nom} · ` : ''}{dateRelative(c.created_at)}
              {(c.user_id === moi.id || moi.role === 'super-admin') && <button type="button" onClick={() => supprimer(c)}>Supprimer</button>}
            </small>
          </div>
        </div>
      ))}
      <div className="fil-cin">
        <Avatar personne={moi} petit />
        <input
          value={saisie} onChange={(e) => setSaisie(e.target.value.slice(0, 2000))} placeholder="Écrire un commentaire…"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envoyer(); } }} disabled={envoi}
        />
      </div>
    </div>
  );
}

// ─── Carte de publication ────────────────────────────────────────────────────

function Post({ post, moi, onChange, onSupprime }) {
  const [commentairesOuverts, setCommentairesOuverts] = useState(false);
  const [visionneuse, setVisionneuse] = useState(null);
  const [menu, setMenu] = useState(false);
  const [edition, setEdition] = useState(false);
  const [texteEdit, setTexteEdit] = useState(post.texte);
  const total = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);
  const peutSupprimer = post.peut_modifier || moi.role === 'super-admin';

  const reagir = async (emoji) => {
    // Retour immédiat, corrigé par la réponse du serveur.
    const avant = { reactions: post.reactions, ma_reaction: post.ma_reaction };
    const r = { ...post.reactions };
    if (post.ma_reaction) r[post.ma_reaction] = Math.max(0, (r[post.ma_reaction] || 1) - 1);
    if (post.ma_reaction !== emoji) r[emoji] = (r[emoji] || 0) + 1;
    onChange({ reactions: r, ma_reaction: post.ma_reaction === emoji ? null : emoji });
    try { const { data } = await filApi.reagir(post.id, emoji); onChange(data); }
    catch { onChange(avant); }
  };
  const epingler = async () => {
    setMenu(false);
    try { await filApi.updatePost(post.id, { epingle: !post.epingle }); onChange({ epingle: !post.epingle }); }
    catch (e) { alert(e.response?.data?.error || 'Action impossible'); }
  };
  const supprimer = async () => {
    setMenu(false);
    if (!window.confirm('Supprimer cette publication ? Les photos et vidéos seront effacées.')) return;
    try { await filApi.removePost(post.id); onSupprime(post.id); }
    catch (e) { alert(e.response?.data?.error || 'Suppression impossible'); }
  };
  const enregistrerTexte = async () => {
    try { await filApi.updatePost(post.id, { texte: texteEdit }); onChange({ texte: texteEdit.trim() }); setEdition(false); }
    catch (e) { alert(e.response?.data?.error || 'Modification impossible'); }
  };

  return (
    <article className={`fil-card fil-post${post.epingle ? ' epingle' : ''}`}>
      {post.epingle && <div className="fil-pin">📌 Épinglé</div>}
      <header className="fil-who">
        <Avatar personne={post.auteur} />
        <div className="fil-who-txt">
          <div className="fil-who-n">{nomAffiche(post.auteur)}<TagPortee post={post} /></div>
          <div className="fil-who-m">
            {[post.auteur.fonction, post.auteur.concession_nom].filter(Boolean).join(' · ')}{post.auteur.fonction || post.auteur.concession_nom ? ' · ' : ''}
            <time dateTime={post.publie_le} title={new Date(post.publie_le).toLocaleString('fr-FR')}>{dateRelative(post.publie_le)}</time>
          </div>
        </div>
        {(peutSupprimer || moi.role === 'super-admin') && (
          <div className="fil-menu">
            <button type="button" className="fil-dots" onClick={() => setMenu((m) => !m)} aria-label="Actions" aria-expanded={menu}>⋯</button>
            {menu && (
              <div className="fil-menu-list" onMouseLeave={() => setMenu(false)}>
                {post.peut_modifier && <button type="button" onClick={() => { setMenu(false); setTexteEdit(post.texte); setEdition(true); }}>✏️ Modifier le texte</button>}
                {moi.role === 'super-admin' && <button type="button" onClick={epingler}>{post.epingle ? '📌 Désépingler' : '📌 Épingler en haut'}</button>}
                {peutSupprimer && <button type="button" className="danger" onClick={supprimer}>🗑️ Supprimer</button>}
              </div>
            )}
          </div>
        )}
      </header>

      {edition ? (
        <div className="fil-edit">
          <textarea value={texteEdit} onChange={(e) => setTexteEdit(e.target.value.slice(0, 5000))} rows={4} autoFocus />
          <div className="fil-tools">
            <button type="button" className="fil-tool fil-tool-annuler" onClick={() => setEdition(false)}>Annuler</button>
            <button type="button" className="fil-tool primaire" onClick={enregistrerTexte}>Enregistrer</button>
          </div>
        </div>
      ) : <TexteRiche texte={post.texte} />}

      <Mosaique medias={post.medias} onOuvrir={(i) => setVisionneuse(i)} />
      {post.lien && <CarteLien lien={post.lien} />}

      <footer className="fil-acts">
        {EMOJIS.map((e) => (
          <button type="button" key={e} className={`fil-re${post.ma_reaction === e ? ' on' : ''}`} onClick={() => reagir(e)} aria-pressed={post.ma_reaction === e}>
            {e}{post.reactions?.[e] ? <span className="fil-re-c">{post.reactions[e]}</span> : null}
          </button>
        ))}
        <button type="button" className="fil-cm" onClick={() => setCommentairesOuverts((o) => !o)}>
          {total > 0 && <span className="fil-total">{total} réaction{total > 1 ? 's' : ''} · </span>}
          {post.nb_commentaires > 0 ? `${post.nb_commentaires} commentaire${post.nb_commentaires > 1 ? 's' : ''}` : 'Commenter'}
        </button>
      </footer>

      {(commentairesOuverts || (post.nb_commentaires > 0 && post.nb_commentaires <= 2)) && (
        <Commentaires post={post} moi={moi} onChange={onChange} />
      )}

      {visionneuse !== null && (
        <Visionneuse
          medias={post.medias} index={visionneuse} onFermer={() => setVisionneuse(null)}
          onNaviguer={(d) => setVisionneuse((i) => (i + d + post.medias.length) % post.medias.length)}
        />
      )}
    </article>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Fil({ user, setHeaderAction }) {
  const [contexte, setContexte] = useState(null);
  const [erreur, setErreur] = useState('');
  const [posts, setPosts] = useState([]);
  const [suivant, setSuivant] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [chargementSuite, setChargementSuite] = useState(false);
  const [filtre, setFiltre] = useState('tout');
  const [concessionFiltre, setConcessionFiltre] = useState('');
  const [nouveaux, setNouveaux] = useState([]);    // ids arrivés par SSE, pas encore affichés
  const [composeurOuvert, setComposeurOuvert] = useState(0);
  const sentinelle = useRef(null);
  const postsRef = useRef(posts);
  postsRef.current = posts;

  const params = useMemo(() => {
    const p = { filtre, limit: 20 };
    if (concessionFiltre) p.concession_id = concessionFiltre;
    return p;
  }, [filtre, concessionFiltre]);

  const charger = useCallback(async (avant) => {
    if (avant) setChargementSuite(true); else setChargement(true);
    try {
      const { data } = await filApi.getPosts(avant ? { ...params, avant } : params);
      setPosts((prev) => (avant ? [...prev, ...data.posts.filter((p) => !prev.some((q) => q.id === p.id))] : data.posts));
      setSuivant(data.suivant);
      setErreur('');
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible de charger le fil');
    } finally { setChargement(false); setChargementSuite(false); }
  }, [params]);

  useEffect(() => {
    let vivant = true;
    filApi.getContexte()
      .then(({ data }) => { if (vivant) setContexte(data); })
      .catch((e) => { if (vivant) setErreur(e.response?.data?.error || 'Le Fil est indisponible'); });
    filApi.marquerVu().catch(() => {});
    window.dispatchEvent(new CustomEvent('fil:vu'));
    return () => { vivant = false; };
  }, []);

  useEffect(() => { if (contexte?.enabled || user?.role === 'super-admin') charger(); }, [contexte, user, charger]);

  // Bouton d'en-tête « + Publier » pour ceux qui ont le droit.
  useEffect(() => {
    if (!setHeaderAction) return;
    if (contexte?.peutPublier) {
      setHeaderAction({ primary: { label: '+ Publier', onClick: () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setComposeurOuvert((n) => n + 1); } } });
    }
    return () => setHeaderAction(null);
  }, [setHeaderAction, contexte]);

  // Temps réel : une publication visible arrive → bandeau (ou insertion si c'est la mienne).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !contexte) return;
    const es = new EventSource(`/api/planning/events?token=${token}`);
    es.addEventListener('fil:post', (ev) => {
      try {
        const d = JSON.parse(ev.data || '{}');
        if (!d.id || d.auteur_id === user?.id || postsRef.current.some((p) => p.id === d.id)) return;
        setNouveaux((n) => (n.includes(d.id) ? n : [...n, d.id]));
      } catch { /* ignoré */ }
    });
    return () => es.close();
  }, [contexte, user]);

  // Défilement infini.
  useEffect(() => {
    if (!sentinelle.current || !suivant) return;
    const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting && !chargementSuite) charger(suivant); }, { rootMargin: '400px' });
    io.observe(sentinelle.current);
    return () => io.disconnect();
  }, [suivant, chargementSuite, charger]);

  const afficherNouveaux = () => { setNouveaux([]); window.scrollTo({ top: 0, behavior: 'smooth' }); charger(); filApi.marquerVu().catch(() => {}); window.dispatchEvent(new CustomEvent('fil:vu')); };
  const majPost = (id, patch) => setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const retirerPost = (id) => setPosts((prev) => prev.filter((p) => p.id !== id));
  const apresPublication = async (id) => {
    try { const { data } = await filApi.getPost(id); setPosts((prev) => [data.post, ...prev.filter((p) => p.id !== id)]); }
    catch { charger(); }
  };
  const basculerFlag = async () => {
    try { const { data } = await filApi.setStatus(!contexte.enabled); setContexte((c) => ({ ...c, enabled: data.enabled })); }
    catch (e) { alert(e.response?.data?.error || 'Impossible de changer le drapeau'); }
  };

  if (erreur && !contexte) return <div className="fil-page"><div className="fil-card fil-vide">{erreur}</div></div>;
  if (!contexte) return <div className="fil-page"><div className="fil-card fil-vide">Chargement du fil…</div></div>;
  const { moi } = contexte;
  const puces = [
    { id: 'tout', label: 'Tout' },
    moi.concession_nom || moi.global ? { id: 'site', label: moi.global ? 'Par concession' : moi.concession_nom } : null,
    { id: 'activite', label: moi.global ? 'Activité' : `Activité ${ACTIVITE_LABEL[moi.activite] || moi.activite || ''}` },
    { id: 'groupe', label: 'Groupe' },
  ].filter(Boolean);

  return (
    <div className="fil-page">
      <div className="fil-feed">
        {!contexte.enabled && moi.role === 'super-admin' && (
          <div className="fil-bandeau">
            <span>Le Fil est <b>désactivé</b> pour les collaborateurs : seuls les super-admins le voient. </span>
            <button type="button" onClick={basculerFlag}>Activer pour tout le monde</button>
          </div>
        )}
        {contexte.peutPublier && <Composeur contexte={contexte} onPublie={apresPublication} ouvertInitial={composeurOuvert} />}

        <div className="fil-chips" role="tablist">
          {puces.map((p) => (
            <button type="button" key={p.id} role="tab" aria-selected={filtre === p.id} className={`fil-chip${filtre === p.id ? ' on' : ''}`}
              onClick={() => { setFiltre(p.id); if (p.id !== 'site') setConcessionFiltre(''); }}>
              {p.label}
            </button>
          ))}
          {moi.global && filtre === 'site' && (
            <select className="fil-select inline" value={concessionFiltre} onChange={(e) => setConcessionFiltre(e.target.value)} aria-label="Concession">
              <option value="">Toutes les concessions</option>
              {contexte.concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          )}
        </div>

        {nouveaux.length > 0 && (
          <button type="button" className="fil-newpill" onClick={afficherNouveaux}>
            ↑ {nouveaux.length} nouvelle{nouveaux.length > 1 ? 's' : ''} publication{nouveaux.length > 1 ? 's' : ''}
          </button>
        )}

        {erreur && <div className="fil-erreur">{erreur}</div>}
        {chargement && posts.length === 0 && <div className="fil-card fil-vide">Chargement…</div>}
        {!chargement && posts.length === 0 && !erreur && (
          <div className="fil-card fil-vide">
            <div className="fil-vide-ico">📰</div>
            <b>Rien pour l’instant par ici.</b>
            <span>{contexte.peutPublier ? 'Soyez le premier à partager une nouvelle.' : 'Les nouvelles de votre concession, de votre activité et du groupe apparaîtront ici.'}</span>
          </div>
        )}
        {posts.map((p) => <Post key={p.id} post={p} moi={moi} onChange={(patch) => majPost(p.id, patch)} onSupprime={retirerPost} />)}
        <div ref={sentinelle} className="fil-sentinelle">{chargementSuite ? 'Chargement…' : suivant ? '' : posts.length > 0 ? 'Vous êtes à jour ✓' : ''}</div>
      </div>

      <aside className="fil-rail">
        <div className="fil-card fil-rail-card">
          <h4>Mon périmètre</h4>
          <div className="fil-rail-l"><span>Concession</span><b>{moi.global ? 'Toutes' : moi.concession_nom || '—'}</b></div>
          <div className="fil-rail-l"><span>Activité</span><b>{moi.global ? 'Toutes' : ACTIVITE_LABEL[moi.activite] || moi.activite || '—'}</b></div>
          <div className="fil-rail-l"><span>Groupe</span><b>Oui</b></div>
        </div>
        <div className="fil-card fil-rail-card">
          <h4>Comment ça marche</h4>
          <p>Chacun voit les publications de sa concession, de son activité et du groupe. Réagissez, commentez : le Fil vit de vos retours.</p>
          {!contexte.peutPublier && <p className="fil-rail-muted">Vous n’avez pas le droit de publier : rapprochez-vous de votre responsable pour l’obtenir.</p>}
          <p className="fil-rail-muted">Droit à l’image : ne publiez que des photos de collègues d’accord pour apparaître.</p>
        </div>
      </aside>
    </div>
  );
}
